/**
 * Club administration — FR-ADMIN-1, FR-AUTH-6.
 *
 * Lifted out of the account screen when the personal page grew a
 * sub-navigation (FR-STAT-18). It was never *about* your account: the roster
 * and the guest list are the club's, and burying them under a password form
 * meant the one screen a member visits to change their password was also the
 * longest screen in the app.
 *
 * Rendered only for admins, and only from the Admin section, so a member never
 * sees a control they cannot use.
 */

import { useEffect, useState } from "react";
import { api, type RosterPlayer } from "../api.js";
import { AttentionPanel } from "./AttentionPanel.js";
import { GuestAdmin } from "./GuestAdmin.js";

export function ClubAdmin({
  onOpenSession,
}: {
  onOpenSession?: (sessionId: string) => void;
}): React.JSX.Element {
  return (
    <>
      {/* First, because it is the reason to open this section at all. */}
      <AttentionPanel onOpenSession={onOpenSession} />
      <ClubRoster />
      {/* Guests were filtered out of the roster entirely, so the only place a
          duplicate showed up was an attendance sheet. */}
      <h2>Guests</h2>
      <GuestAdmin />
    </>
  );
}

/**
 * FR-ADMIN-1, FR-AUTH-6 — the roster, and who can sign in.
 *
 * Most rows will never have a login and that is the expected state, not an
 * incomplete one: playing does not require an account, only entering scores
 * does. The list says "no login" rather than showing an empty column, so
 * nobody reads it as a missing field to go and fill in.
 */
function ClubRoster(): React.JSX.Element {
  const [players, setPlayers] = useState<RosterPlayer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [granting, setGranting] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  /** Shown once, deliberately not stored. Reloading the screen loses it. */
  const [issued, setIssued] = useState<{ name: string; username: string; password: string } | null>(
    null,
  );

  function load(): void {
    api
      .roster()
      .then(setPlayers)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Could not load the roster."));
  }

  useEffect(load, []);

  return (
    <>
      <h2>Club roster</h2>

      {issued ? (
        <div className="block">
          <h3>Password for {issued.name}</h3>
          <div className="stack">
            <p>
              Username <code className="temp-password">{issued.username}</code>
            </p>
            <p>
              Password <code className="temp-password">{issued.password}</code>
            </p>
            <p className="muted small">
              Read this out now — it is not shown again and it is not stored
              anywhere you can read it back. {issued.name} should change it
              under Your account after signing in.
            </p>
            <button className="quiet" onClick={() => setIssued(null)}>
              Done
            </button>
          </div>
        </div>
      ) : null}

      <div className="block">
        <h3>Add a member</h3>
        <div className="stack">
          <input
            value={newName}
            placeholder="Name"
            onChange={(e) => setNewName(e.target.value)}
          />
          <p className="muted small">
            Added without a login. Give them one below only if they will be
            entering scores.
          </p>
          <button
            className="primary"
            disabled={busy || newName.trim() === ""}
            onClick={() => {
              setBusy(true);
              setError(null);
              api
                .addPlayer(newName.trim())
                .then(() => {
                  setNewName("");
                  load();
                })
                .catch((e: unknown) =>
                  setError(e instanceof Error ? e.message : "Could not add that player."),
                )
                .finally(() => setBusy(false));
            }}
          >
            Add
          </button>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <ul className="roster">
        {(players ?? [])
          .filter((p) => !p.isGuest)
          .map((player) => (
            <li key={player.id}>
              <div className="block">
                {/*
                  Not an <h3>: block headings are uppercased section labels,
                  and this row carries a person's name and the username they
                  type. Shouting both made "Amit" and "amit" the same string on
                  screen, which is exactly the distinction that matters here.
                */}
                <div className="roster-head">
                  <span className="roster-name">
                    {player.displayName}
                    {player.role === "admin" ? <span className="guest-flag">admin</span> : null}
                  </span>
                  <code className="roster-username">{player.username ?? "no login"}</code>
                </div>

                {player.username ? (
                  <p>
                    Signs in as {player.username}. They change their own password
                    in the app; you cannot see or set it.
                  </p>
                ) : granting === player.id ? (
                  <div className="stack">
                    <input
                      value={username}
                      placeholder="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      onChange={(e) => setUsername(e.target.value)}
                    />
                    <div className="row">
                      <button
                        className="primary"
                        disabled={busy || username.trim() === ""}
                        onClick={() => {
                          setBusy(true);
                          setError(null);
                          api
                            .grantLogin(player.id, username.trim())
                            .then((result) => {
                              setIssued({
                                name: player.displayName,
                                username: result.player.username ?? username.trim(),
                                password: result.temporaryPassword,
                              });
                              setGranting(null);
                              setUsername("");
                              load();
                            })
                            .catch((e: unknown) =>
                              setError(
                                e instanceof Error ? e.message : "Could not create that login.",
                              ),
                            )
                            .finally(() => setBusy(false));
                        }}
                      >
                        Create login
                      </button>
                      <button className="quiet" onClick={() => setGranting(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="quiet"
                    onClick={() => {
                      setGranting(player.id);
                      // A sensible default they can change; the server folds it
                      // to lower case and rejects anything unusable.
                      setUsername(player.displayName.split(" ")[0].toLowerCase());
                    }}
                  >
                    Give them a login
                  </button>
                )}
              </div>
            </li>
          ))}
      </ul>
    </>
  );
}
